"use client";

import { sendGTMEvent } from "@next/third-parties/google";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useMemo } from "react";
import { z } from "zod";
import Link from "next/link";
import FlightSearchForm from "./FlightSearchForm";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useModal } from "@/contexts/ModalContext";
import { DATA_LAYER_EVENT_NAMES } from "@/constants/dataLayer";
import { cdnCommonPath } from "@/constants/cdnUrls";
import Modal from "@/components/ui/Modal";
import { getLocalizedPath } from "@/utils/pathUtils";
import { PATHS } from "@/constants/pathnames";
import { useRouter } from "@/i18n/navigation";
import DynamicForm from "@/components/dynamicForm";

const ReservationSearchForm = ({
  t,
  description,
  link,
  buttonText,
  isCheckIn = false,
  onClose,
}) => {
  const router = useRouter();
  const { locale } = useLocale();
  const schema = useMemo(
    () =>
      z.object({
        pnrCode: z
          .string()
          .trim()
          .min(1, t("common.reservationNumberRequired"))
          .max(20, t("pnrFormPnrCodeTooLong")),
        productType: z.string().optional(),
        surname: z
          .string()
          .trim()
          .min(1, t("common.lastNameRequired"))
          .max(50, t("pnrFormSurnameTooLong")),
      }),
    [t],
  );

  const reservationUrl = getLocalizedPath("/reservation/[hashCode]", locale);

  const handleTrack = (data) => {
    if (isCheckIn) {
      sendGTMEvent({
        event: "custom_event",
        event_category: "custom_event",
        event_action:
          DATA_LAYER_EVENT_NAMES.ONLINE_CHECKIN_RESERVATION_REDIRECT,
        flight_number: data?.pnrCode,
      });
    } else {
      sendGTMEvent({
        event: "custom_event",
        event_category: "custom_event",
        event_action: DATA_LAYER_EVENT_NAMES.CHECK_RESERVATION,
        pnr_number: data?.pnrCode,
        reservation_category: "flight",
      });
    }
  };

  return (
    <div>
      <DynamicForm
        mode="onSubmit"
        schema={schema}
        dryRun={false}
        endpoint="/api/reservation/pnr"
        defaultValues={{ productType: "Flight", pnrCode: "", surname: "" }}
        onSuccess={(res) => {
          const url = reservationUrl.replace("[hashCode]", res?.code || "");
          router.push(url);
          onClose();
        }}
        onError={(msg) => {
          console.warn("PNR Search error:", msg);
        }}
        onSubmit={handleTrack}
        className="space-y-4"
      >
        <DynamicForm.Error className="rounded bg-red-50 px-3 py-2 text-sm text-red-700" />

        <DynamicForm.Field
          name="pnrCode"
          type="text"
          label={t("common.reservationNumber")}
        />

        <div className="hidden">
          <DynamicForm.Field name="productType" type="hidden" />
        </div>

        <DynamicForm.Field
          name="surname"
          type="text"
          label={t("common.lastName")}
        />

        <div className="flex flex-col gap-2">
          <DynamicForm.Button
            spanClassName="h-[50px] flex items-center justify-center text-center font-medium"
            className="bg-primary hover:bg-primary/90 flex-1 rounded-lg px-4 text-white"
          >
            {buttonText}
          </DynamicForm.Button>

          <p
            className={
              "text-foreground-muted text-[16px] leading-[21px] font-normal"
            }
          >
            {t(description)}{" "}
            {link && (
              <Link
                className={"text-primary underline"}
                href={getLocalizedPath(PATHS.FAQ_PAGE.PATH, locale)}
              >
                {t(link)}
              </Link>
            )}
          </p>
        </div>
      </DynamicForm>
    </div>
  );
};

const FlightSearch = ({ initialDepartureCityCode, initialReturnCityCode }) => {
  const t = useTranslationDetector();
  const locale = useLocale();
  const reservationSearchModal = useModal("reservation-search-modal");
  const onlineCheckInModal = useModal("online-check-in-modal");

  const openReservationSearchModal = () => {
    reservationSearchModal.open({
      title: t("common.reservationSearch"),
      isBottomSheet: true,
    });
  };
  const openOnlineCheckInModal = () => {
    onlineCheckInModal.open({
      title: t("common.onlineCheckIn"),
      isBottomSheet: true,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <FlightSearchForm
        initialDepartureCityCode={initialDepartureCityCode}
        initialReturnCityCode={initialReturnCityCode}
      />

      <div className="shadow-small flex items-center justify-center rounded-lg bg-white/90 md:hidden">
        <button
          className="inline-flex w-1/2 items-center justify-center gap-4 p-6 text-sm font-medium"
          onClick={openReservationSearchModal}
        >
          <figure className="relative flex h-10 min-h-10 w-10 min-w-10 items-center justify-center overflow-hidden">
            <Image
              src={`${cdnCommonPath}onlinecancel.svg?v=124`}
              alt="PNR Search"
              width={0}
              height={0}
              sizes="100%"
              className="h-full w-full"
            />
          </figure>
          <span className="text-left break-keep">{t("flight.pnrSearch")}</span>
        </button>
        <span className="bg-primary-weak h-[42px] w-px"></span>
        <button
          className="inline-flex w-1/2 items-center justify-center gap-4 p-6 text-sm font-medium"
          onClick={openOnlineCheckInModal}
        >
          <figure className="relative flex h-10 min-h-10 w-10 min-w-10 items-center justify-center overflow-hidden">
            <Image
              src={`${cdnCommonPath}onlinecheckin.svg?v=124`}
              alt="Check-In"
              width={0}
              height={0}
              sizes="100%"
              className="h-full w-full"
            />
          </figure>
          <span className="text-left break-keep">
            {t("flight.onlineCheckin").replace("-", "\u2011")}
          </span>
        </button>
      </div>
      <Modal id="reservation-search-modal">
        <ReservationSearchForm
          description={t("common.reservationSearchDescription")}
          link={t("common.reservationSearchLink")}
          buttonText={t("common.pnrSearch")}
          t={t}
          locale={locale}
          isCheckIn={false}
          onClose={() => {
            reservationSearchModal.close();
          }}
        />
      </Modal>
      <Modal id="online-check-in-modal">
        <ReservationSearchForm
          description={t("common.reservationSearchDescription")}
          link={t("common.reservationSearchLink")}
          buttonText={t("common.onlineCheckIn")}
          t={t}
          locale={locale}
          isCheckIn={true}
          onClose={() => {
            onlineCheckInModal.close();
          }}
        />
      </Modal>
    </div>
  );
};

export default FlightSearch;
